import { tokenDeclarations } from './tokens.js';

/**
 * ビューアのスタイル。
 *
 * **Shadow root の内側に閉じる**([ADR-0017](../../../../docs/adr/0017-viewer-embed-boundary.md))。
 * 埋め込み先のグローバル CSS を汚さず、埋め込み先の CSS からも影響を受けない。
 *
 * V02(単体ページ)は Shadow を使わないが、**同じスタイル文字列を
 * `<style>` に入れて共有する** —— 見た目が V01 と一致していることが
 * 「iframe 埋め込みの実体」(ADR-0003)として成立する前提。
 */
/**
 * ステージの高さをビューポートに収める上限(#212)。**この順に適用する。**
 *
 * ## 順序が意味を持つ
 *
 * **新しい単位を後に置く。** 同じプロパティに順に代入すると、
 * **非対応のブラウザは解釈できない値を捨てる** —— 後ろが捨てられて
 * 前が残る。逆に並べると、**`dvh` に対応したブラウザで `vh` が勝ってしまう。**
 *
 * ## `vh` だけでは足りない
 *
 * モバイルの `vh` は**アドレスバーを含まない最大の高さ**で、バーが出ている
 * 間は画面より大きい —— それだけでスクロールが生まれる。報告された
 * 「タブなどではみ出す」がまさにこれ。**`dvh` はいま見えている高さに追従する。**
 *
 * ## `vh` も外せない
 *
 * `dvh` は `vh` より新しく、**古い環境では無効値として捨てられる** ——
 * 落ちる先が無いと高さが無制限に戻る。
 */
export const VIEWPORT_HEIGHT_LIMITS = ['100vh', '100dvh'] as const;

/**
 * 画面に収める上限から、**ページの見出しなどの高さ(px)を差し引いたもの**(#267)。
 * **順序は [`VIEWPORT_HEIGHT_LIMITS`](#VIEWPORT_HEIGHT_LIMITS) と同じ**(`vh` → `dvh`)。
 *
 * **0 なら従来の値をそのまま返す** —— 盤面の単体ページは変わらない。
 * 共有フォルダのページは、見出しとタブの帯の高さを渡す。
 *
 * **`var()` を使わない。** `var()` を含む宣言は代入した時点では検査されず、`dvh` に
 * 対応していないブラウザでも後ろの宣言が受け入れられる —— 値を計算する時点で無効になり、
 * **前の `vh` に戻らず上限ごと消える。** 数値を埋め込めば、非対応のブラウザは `dvh` の
 * 代入を捨て、前の `vh` が残る。
 */
export function viewportHeightLimits(offsetPx: number): readonly string[] {
  // **切り上げる。** 切り捨てると差し引きが足りず、1px はみ出してスクロールが出ることがある。
  const offset = Number.isFinite(offsetPx) ? Math.max(0, Math.ceil(offsetPx)) : 0;
  if (offset === 0) return VIEWPORT_HEIGHT_LIMITS;
  return VIEWPORT_HEIGHT_LIMITS.map((limit) => `calc(${limit} - ${String(offset)}px)`);
}

export function viewerStyles(): string {
  return `
:host, .signa-root {
${tokenDeclarations()}
  display: block;
  position: relative;
  width: 100%;
  color: var(--sp-text-primary);
  font-family: system-ui, -apple-system, 'Segoe UI', 'Hiragino Sans', 'Noto Sans JP', sans-serif;
  font-size: 14px;
  line-height: 1.5;
  box-sizing: border-box;
}

.signa-root *, .signa-root *::before, .signa-root *::after {
  box-sizing: inherit;
}

.signa-frame {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 100%;
  min-height: 120px;
  background: var(--sp-surface-page);
  border-radius: var(--signa-radius);
  overflow: hidden;
}

/* 状態表示(ローディング・エラー)。ステージの上に重ねる。 */
.signa-status {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px;
  text-align: center;
  color: var(--sp-text-secondary);
}

.signa-status[hidden] { display: none; }

.signa-status-error { color: var(--sp-text-primary); }

.signa-button {
  appearance: none;
  padding: 8px 16px;
  border: 1px solid var(--sp-border-strong);
  border-radius: var(--signa-radius);
  background: var(--sp-surface-raised);
  color: var(--sp-text-primary);
  font: inherit;
  cursor: pointer;
}

.signa-button:hover { background: var(--sp-surface-overlay-weak); }

.signa-button:focus-visible {
  outline: 2px solid var(--signa-accent);
  outline-offset: 2px;
}

/* D&D の受け口。ステージの上に重ねる。 */
.signa-ui {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px;
  pointer-events: none;
}

/* 受け口自体はクリックできる必要がある。 */
.signa-ui > * { pointer-events: auto; }

/*
 * D&D の見せ方(受け口の案内とドロップ先の輪郭)。
 *
 * pointer-events を切る。この要素は inset:0 で UI 全体を覆い、
 * position 指定があるぶん静的な兄弟(操作の帯)より上に描かれる ——
 * auto のままだと再生 / 停止 / 同期のクリックを奪う(#159 の退行)。
 *
 * D&D の受け口は signa-root なので、ここが受ける必要はない。
 */
.signa-dropzone {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

/* 操作の帯は覆いより上に置く(押せることを位置でも担保する)。 */
.signa-actions {
  position: relative;
  z-index: 1;
}

/*
 * **画面全体は縁取らない**(#124)。ドロップ先の枠だけを光らせる ——
 * 全体を覆うと「どこに落とせばよいか」が分からない。
 * 残すのは注意文(signa-dropzone-label)の表示だけ。
 */
.signa-drop-outline {
  position: absolute;
  pointer-events: none;
  overflow: visible;
  opacity: 0;
  transition: opacity 120ms ease;
}

.signa-dropzone[data-active='true'] .signa-drop-outline { opacity: 1; }

.signa-drop-outline-shape {
  fill: var(--sp-accent-primary-surface);
  stroke: var(--signa-accent);
  stroke-width: 3;
  stroke-dasharray: 8 5;
}

.signa-dropzone-label {
  padding: 8px 12px;
  border-radius: var(--signa-radius);
  background: var(--sp-surface-overlay-strong);
  color: var(--sp-text-primary);
  opacity: 0;
  transition: opacity 120ms ease;
}

.signa-dropzone[data-active='true'] .signa-dropzone-label { opacity: 1; }

/*
 * **空のセルに出す案内**(#208)。描画コアが SVG の text として置く。
 *
 * **静止状態で出る**のが要点。signa-dropzone-label はドラッグ中しか
 * 見えず(data-active)、**すでにドラッグしている人にしか届かない** ——
 * こちらは何もしていない訪問者に「ここに入れられる」と伝える。
 *
 * **見た目は描画コアに持たせない。** 埋め込み先が調整できる口を残すため
 * (--signa-accent と同じ考え方 — #33)。
 */
.signa-outline-hint {
  /*
   * **黒文字に白ふち**(#234)。以前は白文字 + 白 20% の縁で、**縁が地の
   * 役目を果たしていなかった** —— 明るい背景写真(白い壁・空・照明の当たった
   * 床)の上で、文字と縁がまとめて地に溶けていた。
   *
   * surface-bright(明るい地)と text-on-bright(明るい地の上のテキスト)は
   * **正本が対で定義している組み合わせ**。縁を地、字をその上の文字として扱う。
   *
   * paint-order: stroke は必須 —— 既定では縁が字の上に塗られ、
   * **stroke-width: 4 が字面を食い潰す。**
   */
  fill: var(--sp-text-on-bright);
  font-size: 13px;
  paint-order: stroke;
  stroke: var(--sp-surface-bright);
  stroke-width: 4;
  stroke-linejoin: round;
  /* 枠は pointer-events: none の SVG の中にある。文字も同じ。 */
  user-select: none;
}

/*
 * **どちらを見せるかは入力方式で決める**(#208)。
 *
 * **タッチ端末では D&D で素材を入れられない**(#159)ので、「ここにドロップ」
 * ではなく「PC で試せる」と伝える。
 *
 * **JS で判定しない。** ontouchstart や maxTouchPoints は**タッチ対応の
 * ノート PC を誤判定**するうえ、判定した時点の状態で固定される ——
 * メディアクエリなら**いま使っている入力方式**に追従する。
 */
.signa-outline-hint-touch {
  display: none;
}

@media (hover: none) and (pointer: coarse) {
  .signa-outline-hint-drop {
    display: none;
  }

  .signa-outline-hint-touch {
    display: block;
  }
}

/* 「送信されません」の注意。**素材を当てるまで表示**(#159 で常時表示から変更)。 */
.signa-notice {
  padding: 4px 10px;
  border-radius: var(--signa-radius);
  background: var(--sp-surface-overlay-strong);
  color: var(--sp-text-primary);
  font-size: 12px;
}

.signa-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  /* 埋め込み先が狭いこともある。**折り返してページを押し広げない**(#159)。 */
  flex-wrap: wrap;
  justify-content: center;
}

/*
 * 書き出し中のオーバーレイ(#233)。
 *
 * **ビューアの枠を覆う。** position: absolute にしてあるのは、
 * **fixed だと埋め込み先のページ全体を覆ってしまう**ため —— V01 は他社サイトの中で動く。
 *
 * **盤面がうっすら見える程度に透かす。** 書き出し中は描画を止める(setPaused)ので
 * canvas は空白になりうるが、**完全に塗り潰すと何を書き出しているのか分からない。**
 */
.signa-export-overlay {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 24px;
  /* --sp-surface-page(#2b2b2f)と同じ色。**透かすので rgba で書く。** */
  background: rgba(43, 43, 47, 0.88);
  color: var(--sp-text-primary);
  /* **ここがクリックを受け止める。** 下の操作へ通さない(#233)。 */
  pointer-events: auto;
  /*
   * **低い枠で詰めるための入れ物**(#354 —— 下の \`@container\`)。
   * 大きさは枠で決まる(\`inset: 0\`)ので、中身を見ない \`size\` でも寸法は変わらない。
   */
  container: signa-export / size;
}

.signa-export-overlay[hidden] {
  display: none;
}

.signa-export-label {
  font-size: 15px;
  font-variant-numeric: tabular-nums;
}

/* **幅は枠に対する割合。** 狭い埋め込み先でもはみ出さない。 */
.signa-export-bar {
  width: min(320px, 70%);
  height: 10px;
  border-radius: 999px;
  overflow: hidden;
  background: var(--sp-surface-overlay-weak);
  border: 1px solid var(--sp-border-default);
}

/*
 * 進捗の塗り。**埋め込み先が --signa-accent で色を変えられる**
 * (トークンの公開口 —— tokens.ts)。
 */
.signa-export-fill {
  height: 100%;
  width: 0%;
  background: var(--signa-accent);
  transition: width 120ms linear;
}

/* **改行を表示に残す**(#354)。操作の案内とタブの案内を 1 つの要素に 2 行で並べている。 */
.signa-export-note {
  font-size: 12px;
  color: var(--sp-text-secondary);
  text-align: center;
  white-space: pre-line;
}

/*
 * **低い枠では詰める**(#354)。**上の各規則より後に置く**(同じ詳細度なので、後ろが勝つ)。
 *
 * 注意書きが 2 行に増え、**高さ 135〜160px 前後の埋め込みで上下が切れていた**
 * (修正前は収まっていた)。間隔(\`gap\`)は入れ物自身の指定で、ここからは変えられないので、
 * **子に負の余白を付けて 16px → 4px に詰める。** 収まる高さの枠では見た目を変えない。
 *
 * **測られるのは内容の高さ**(枠から上下の \`padding\` 24px ずつを引いた値)。
 * 120px は枠で 168px —— 16:9 なら幅 300px 前後より狭い埋め込みで詰まる。
 */
@container signa-export (max-height: 120px) {
  .signa-export-overlay > * {
    margin-block: -6px;
  }

  /* 幅も狭いと注意書きが何行にも折り返す。**字と行の間を少し詰める。** */
  .signa-export-note {
    font-size: 11px;
    line-height: 1.3;
  }
}

/*
 * 書き出しの前の確認(#259)。**\`showModal\` で最上位のレイヤーに出る。**
 *
 * **\`<dialog>\` 自体に余白を持たせない** —— \`<dialog>\` 自身へのクリックを
 * 背景のクリックとして扱うため(notice-dialog.ts)。中身は内側の箱に置く。
 */
.signa-dialog {
  width: min(440px, calc(100vw - 32px));
  max-width: none;
  padding: 0;
  border: 1px solid var(--sp-border-default);
  border-radius: var(--signa-radius);
  background: var(--sp-surface-page);
  color: var(--sp-text-primary);
  font: inherit;
}

/* --sp-surface-page(#2b2b2f)よりも暗く沈める。**::backdrop はトークンを継がない環境がある。** */
.signa-dialog::backdrop {
  background: rgba(0, 0, 0, 0.6);
}

.signa-dialog-panel {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
}

.signa-dialog-body {
  margin: 0;
  white-space: pre-line;
  line-height: 1.7;
}

.signa-dialog-actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}

/*
 * 確定ボタン。社内 SPA の強調表示(緑の枠 + 薄い緑の面 + 緑の文字)に合わせる。
 *
 * **--signa-accent には既定値を添える。** 未指定の環境で値が空になっても、
 * 確定ボタンが他のボタンと見分けられなくならないように。
 */
.signa-button-primary {
  border-color: var(--signa-accent, var(--sp-accent-primary));
  background: var(--sp-accent-primary-surface);
  color: var(--signa-accent, var(--sp-accent-primary));
}

.signa-button-primary:hover { background: var(--sp-accent-primary-surface); filter: brightness(1.2); }

/* 再生 / 停止 / 同期(#159)。**素材が当たるまで出さない。** */
.signa-controls {
  display: flex;
  gap: 8px;
  align-items: center;
}

/*
 * display を指定した要素には [hidden] を書き添える。
 *
 * hidden 属性の display:none は UA スタイルシートのもので、
 * 作者の display:flex に負ける —— 書かないと hidden にしても消えない。
 *
 * .signa-ui はこれが抜けていて、動画枠が 0 個のときに D&D の受け口が
 * 消えていなかった(#159 で気付いた既存の不具合)。
 */
.signa-ui[hidden],
.signa-dropzone[hidden],
.signa-actions[hidden],
.signa-controls[hidden] { display: none; }

.signa-notice[hidden] { display: none; }

.signa-button[disabled] {
  opacity: 0.5;
  cursor: default;
}

.signa-button[disabled]:hover { background: var(--sp-surface-raised); }

.signa-message {
  padding: 4px 10px;
  border-radius: var(--signa-radius);
  background: var(--sp-surface-overlay-strong);
  font-size: 12px;
}

.signa-message[hidden] { display: none; }

/*
 * 素材の要素の置き場。
 *
 * **display: none にしない**(復号が止まる実装がある)。
 *
 * **面積を潰さない**(#168 / PoC 3.5 の申し送り 1)。以前は 1px で、
 * 事実上不可視の動画の復号優先度が下げられている疑いがあった ——
 * 「一部の動画だけ進みが遅い」の原因候補。小さく保ちつつ面積を持たせる。
 *
 * **並べる。重ねない。** 全部を同じ位置に置くと 1 本を除いて完全に隠れ、
 * 「不可視なら優先度が下がる」という仮説のもとでは**まさに一部だけが遅くなる。**
 */
.signa-videos {
  position: absolute;
  left: 0;
  top: 0;
  width: 160px;
  height: 90px;
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

@media (prefers-reduced-motion: reduce) {
  .signa-dropzone, .signa-dropzone-label, .signa-drop-outline { transition: none; }
}
`.trim();
}
